import { type ReactNode } from "react";
import { cn } from "@/lib/utils";

interface ChartCardProps {
  title: string;
  subtitle?: string;
  /** Right-aligned element in the header (e.g. a badge or action). */
  headerRight?: ReactNode;
  /** Rendered above the chart body, below the header (e.g. inline legend). */
  headerBelow?: ReactNode;
  children: ReactNode;
  /** Set true to remove body padding — lets children bleed to the card edges. */
  flush?: boolean;
  className?: string;
  /**
   * Minimum height of the whole card (not the body).
   * Body area fills the remaining space via `flex-1`, so charts that pass
   * `h-full` to their inner container will expand to fit any stretched
   * grid-row height.
   */
  minHeight?: number;
  /**
   * Fixed card height. Use when the card should NOT grow to fit content —
   * e.g. a table with many rows that should scroll inside the card. When
   * set, cascades a definite height down the flex chain so descendants'
   * `h-full` + `overflow-y-auto` actually scroll.
   */
  height?: number;
  ariaLabel?: string;
}

/**
 * Standard card wrapper for chart panels.
 *
 * Lays out as a vertical flex container so body children that use `h-full`
 * expand to fill whatever the grid row stretches the card to. Prevents
 * empty-whitespace-below-charts when a sibling card in the row happens to
 * be naturally taller.
 */
export function ChartCard({
  title,
  subtitle,
  headerRight,
  headerBelow,
  children,
  flush = false,
  className,
  minHeight,
  height,
  ariaLabel,
}: ChartCardProps) {
  const style: React.CSSProperties = {};
  if (height !== undefined) style.height = height;
  else if (minHeight !== undefined) style.minHeight = minHeight;
  return (
    <section
      className={cn(
        "bg-white border border-[var(--color-border-light)] rounded-xl overflow-hidden",
        "flex flex-col",
        flush ? "" : "p-5",
        className
      )}
      style={Object.keys(style).length ? style : undefined}
      aria-label={ariaLabel ?? title}
    >
      <header
        className={cn("flex items-start justify-between gap-3 flex-shrink-0", flush && "px-5 pt-5")}
      >
        <div className="min-w-0">
          <h3 className="text-[13px] font-semibold text-[var(--color-text-primary)] leading-tight">
            {title}
          </h3>
          {subtitle && (
            <p className="text-[11px] text-[var(--color-text-tertiary)] mt-0.5">{subtitle}</p>
          )}
        </div>
        {headerRight && <div className="flex-shrink-0">{headerRight}</div>}
      </header>

      {headerBelow && (
        <div className={cn("mt-3 flex-shrink-0", flush && "px-5")}>{headerBelow}</div>
      )}

      <div className="mt-4 flex-1 min-h-0">{children}</div>
    </section>
  );
}
